<template>
  <div class="flex flex-col gap-4">
    <div class="text-black-950">Select Themes</div>
    <div class="flex flex-wrap gap-4">
      <!-- solid colors -->
      <div
        v-for="theme in availableThemes"
        :key="theme.name"
        :style="{
          borderColor: theme.colors['--color-primary-brand'] || '#178a7c',
        }"
        :class="[
          'flex items-center p-2 !border rounded-md cursor-pointer  w-48',
          currentTheme === theme.name ? '!bg-brand-100' : 'hover:opacity-80',
        ]"
        @click="selectTheme(theme.name)"
      >
        <div
          :style="{
            background: theme.colors['--color-primary-brand'],
            width: '32px',
            height: '32px',
            borderRadius: '50%',
            marginRight: '4px',
          }"
          class="w-8 h-8 rounded-full mr-1"
        ></div>
        <div class="text-black-800">
          {{ formatThemeName(theme.name) }}
        </div>
      </div>
      <div
        :style="{
          borderColor: currentThemeColor || '#000000',
        }"
        :class="['flex items-center p-2 !border rounded-md cursor-pointer  w-70 hover:opacity-80']"
      >
        <el-input
          type="color"
          class="fw-bold w-8 h-8 rounded-full mr-1"
          v-model="customColor"
          style="width: 100%"
          @input="createPalatte"
        ></el-input>
        <div class="text-black-800 whitespace-nowrap">Select A Custom Theme</div>
      </div>
    </div>
    <el-select v-model="themeType" placeholder="Theme Type" @change="populateThemeType">
      <el-option
        v-for="item in options"
        :key="item.Value"
        :label="item.label"
        :value="item.value"
      ></el-option>
    </el-select>
    <!-- Light/Dark Mode Switch -->
    <div class="hidden">
      <!-- class="flex items-center justify-start gap-4 mt-2 " -->
      <p class="brand-900">Switch To {{ themeMode == 'light' ? 'Dark' : 'Light' }} Mode :</p>
      <button
        :class="[
          'flex items-center gap-2 px-4 py-2 rounded-full border transition',
          themeMode === 'light'
            ? 'bg-white border-brand-500 shadow text-black'
            : 'bg-gray-100 border-gray-300 text-gray-500 hover:bg-gray-200',
        ]"
        @click="setLightTheme"
        aria-label="Light Mode"
        v-if="themeMode == 'dark'"
      >
        <i class="el-icon-sunrise text-lg"></i>
        <span class="hidden sm:inline font-medium">Light</span>
      </button>
      <button
        v-else
        :class="[
          'flex items-center gap-2 px-4 py-2 rounded-full border transition',
          themeMode === 'dark'
            ? 'bg-gray-900 border-brand-500 shadow text-white'
            : 'bg-gray-100 border-gray-300 text-gray-500 hover:bg-gray-200',
        ]"
        @click="setDarkTheme"
        aria-label="Dark Mode"
      >
        <i class="el-icon-moon text-lg"></i>
        <span class="hidden sm:inline font-medium">Dark</span>
      </button>
    </div>

    <!-- Text Size Settings -->
    <div class="text-black-950 mt-4">Text Size Settings</div>
    <div class="flex flex-wrap gap-4 mt-2">
      <div class="flex-1 min-w-48">
        <label class="block text-sm text-black-700 mb-1">Label Size</label>
        <el-select v-model="textSettings.labelSize" placeholder="Select Label Size" class="w-full">
          <el-option
            v-for="size in labelSizeOptions"
            :key="size.value"
            :label="size.label"
            :value="size.value"
          />
        </el-select>
      </div>
      <div class="flex-1 min-w-48">
        <label class="block text-sm text-black-700 mb-1">Data Size</label>
        <el-select v-model="textSettings.dataSize" placeholder="Select Data Size" class="w-full">
          <el-option
            v-for="size in dataSizeOptions"
            :key="size.value"
            :label="size.label"
            :value="size.value"
          />
        </el-select>
      </div>
    </div>

    <!-- Font Weight Settings -->
    <div class="text-black-950 mt-4">Font Weight Settings</div>
    <div class="flex flex-wrap gap-4 mt-2">
      <div class="flex-1 min-w-48">
        <label class="block text-sm text-black-700 mb-1">Label Weight</label>
        <el-select
          v-model="textSettings.labelWeight"
          placeholder="Select Label Weight"
          class="w-full"
        >
          <el-option
            v-for="weight in fontWeightOptions"
            :key="weight.value"
            :label="weight.label"
            :value="weight.value"
          />
        </el-select>
      </div>
      <div class="flex-1 min-w-48">
        <label class="block text-sm text-black-700 mb-1">Data Weight</label>
        <el-select
          v-model="textSettings.dataWeight"
          placeholder="Select Data Weight"
          class="w-full"
        >
          <el-option
            v-for="weight in fontWeightOptions"
            :key="weight.value"
            :label="weight.label"
            :value="weight.value"
          />
        </el-select>
      </div>
    </div>

    <!-- Color Settings -->
    <div class="text-black-950 mt-4">Text Color Settings</div>
    <div class="flex flex-wrap gap-4 mt-2">
      <div class="flex-1 min-w-48">
        <label class="block text-sm text-black-700 mb-1">Label Color</label>
        <div class="flex items-center border rounded-md p-2">
          <el-input
            type="color"
            class="w-8 h-8 rounded mr-2"
            v-model="textSettings.labelColor"
            style="width: 32px; height: 32px"
          />
          <span class="text-black-700">Select Label Color</span>
        </div>
      </div>
      <div class="flex-1 min-w-48">
        <label class="block text-sm text-black-700 mb-1">Data Color</label>
        <div class="flex items-center border rounded-md p-2">
          <el-input
            type="color"
            class="w-8 h-8 rounded mr-2"
            v-model="textSettings.dataColor"
            style="width: 32px; height: 32px"
          />
          <span class="text-black-700">Select Data Color</span>
        </div>
      </div>
    </div>

    <div class="mt-4">
      <el-button type="primary" @click="saveChanges">Save</el-button>
      <el-button
        class="border border-page-border hover:!bg-white hover:!text-brand-500 hover:!border hover:!border-brand-500"
        @click="cancelSaved"
      >
        cancel
      </el-button>
    </div>
  </div>
</template>
<script>
  import { mapActions, mapGetters } from 'vuex';
  import NotificationHelper from '@/mixins/NotificationHelper';
  import chroma from 'chroma-js';

  export default {
    name: 'ThemePicker',
    mixins: [NotificationHelper],
    data: () => ({
      currentTheme: null,
      customColor: '',
      palette: {},
      themeType: '',
      themeMode: '',
      textSettings: {
        labelSize: '14px',
        dataSize: '16px',
        labelWeight: '400',
        dataWeight: '400',
        labelColor: '#6D6D6D',
        dataColor: '#4F4F4F',
      },
      options: [
        {
          value: 'transparent',
          label: 'Transparent',
        },
        {
          value: 'solid',
          label: 'Solid',
        },
        {
          value: 'solid_gradient',
          label: 'Solid Gradient',
        },
      ],
      labelSizeOptions: [
        { value: '10px', label: '10px - Extra Small' },
        { value: '12px', label: '12px - Small' },
        { value: '14px', label: '14px - Default' },
        { value: '16px', label: '16px - Medium' },
        { value: '18px', label: '18px - Large' },
        { value: '20px', label: '20px - Extra Large' },
        { value: '24px', label: '24px - Huge' },
      ],
      dataSizeOptions: [
        { value: '12px', label: '12px - Small' },
        { value: '14px', label: '14px - Default' },
        { value: '16px', label: '16px - Medium' },
        { value: '18px', label: '18px - Large' },
        { value: '20px', label: '20px - Extra Large' },
        { value: '22px', label: '22px - Huge' },
        { value: '24px', label: '24px - Extra Huge' },
      ],
      fontWeightOptions: [
        { value: '100', label: '100 - Thin' },
        { value: '200', label: '200 - Extra Light' },
        { value: '300', label: '300 - Light' },
        { value: '400', label: '400 - Normal' },
        { value: '500', label: '500 - Medium' },
        { value: '600', label: '600 - Semi Bold' },
        { value: '700', label: '700 - Bold' },
        { value: '800', label: '800 - Extra Bold' },
        { value: '900', label: '900 - Black' },
      ],
    }),
    mounted() {
      this.initializeTheme();
      this.palette = this.getThemePalatte || {};
      this.customColor = this.currentThemeColor;
      this.themeType = this.getThemeType || 'transparent';
      this.themeMode = this.getThemeMode || 'light';

      // Initialize text settings from store
      if (this.getTextSettings) {
        this.textSettings = { ...this.getTextSettings };
      }
      this.applyTextSettings();
    },
    computed: {
      ...mapGetters('theme', [
        'getCurrentTheme',
        'getAvailableThemes',
        'getThemePalatte',
        'getThemeType',
        'getThemeMode',
        'getTextSettings',
      ]),
      availableThemes() {
        return this.getAvailableThemes;
      },
      selectedTheme() {
        return this.currentTheme;
      },
      currentThemeColor() {
        return this.getThemePalatte?.['--color-primary-brand'] || '#178a7c';
      },
      currentThemePalatte() {
        return this.getThemePalatte || {};
      },
    },
    created() {
      this.initializeTheme();
    },
    watch: {
      textSettings: {
        handler() {
          this.applyTextSettings();
        },
        deep: true,
        immediate: false,
      },
      getThemePalatte: {
        handler(newPalette) {
          if (newPalette && newPalette['--color-primary-brand']) {
            this.customColor = newPalette['--color-primary-brand'];
            this.palette = newPalette;
          }
        },
        deep: true,
        immediate: false,
      },
    },
    methods: {
      ...mapActions('theme', ['updateTheme', 'fetchThemes']),
      populateThemeType(value) {
        this.themeType = value;
        this.$nextTick(() => {
          this.createPalatte();
        });
        this.$store.commit('theme/SET_CURRENT_THEME_TYPE', value);
      },
      createPalatte() {
        if (this.themeType === 'transparent' && this.themeMode === 'light') {
          this.palette = this.setLightMode(this.customColor);
        } else if (this.themeType === 'transparent' && this.themeMode === 'dark') {
          this.palette = this.setDarkMode(this.customColor);
        } else if (this.themeType === 'solid' && this.themeMode === 'light') {
          this.palette = this.setSolidMode(this.customColor);
        } else if (this.themeType === 'solid' && this.themeMode === 'dark') {
          this.palette = this.setSolidDarkMode(this.customColor);
        } else if (this.themeType === 'solid_gradient' && this.themeMode === 'light') {
          this.palette = this.setSolidGradientMode(this.customColor);
        } else if (this.themeType === 'solid_gradient' && this.themeMode === 'dark') {
          this.palette = this.setSolidGradientDarkMode(this.customColor);
        }
        this.applyColors(this.palette);
      },
      setLightMode(baseColor) {
        // Use provided baseColor or fallback
        const base = baseColor || '#178a7c';
        const scale = chroma.scale([base, '#ffffff']).mode('lab').colors(6);
        const palette = {
          '--color-primary-brand': base,
          '--color-brand-500': scale[0],
          '--color-brand-400': scale[1],
          '--color-brand-300': scale[2],
          '--color-brand-200': scale[3],
          '--color-brand-100': scale[4],
          '--color-brand-50': scale[5],
          '--page-background': '#F3F5F6',
          '--container-background': '#FFFFFF',
          '--page-border': '#E9EBEC',
          '--card-color': '#FFFFFF',
          '--card-border': '#E9EBEC',
          '--filter-border': '#CDCDCD',
          '--color-black': '#000000',
          '--color-white': '#FFFFFF',
          '--color-black-950': '#1F1F1F',
          '--color-black-900': '#3D3D3D',
          '--color-black-800': '#454545',
          '--color-black-700': '#4F4F4F',
          '--color-black-600': '#5D5D5D',
          '--color-black-500': '#6D6D6D',
          '--color-black-400': '#888888',
          '--color-black-300': '#B0B0B0',
          '--color-black-200': '#D1D1D1',
          '--color-black-150': '#EBEBEB',
          '--color-black-100': '#F1F1F1',
          '--color-black-50': '#F6F6F6',
          '--color-hyperlink': '#1460ED',
          '--color-active': '#27AE60',
          '--color-inactive': '#EB5757',
          '--color-assign': '#399761',
          '--color-note': '#FF881F',
          '--color-ai-gradient': '#1460ED',
        };
        return palette;
      },
      setDarkMode(baseColor) {
        const base = chroma(baseColor || '#2F80ED');

        // Generate darker brand shades manually by adjusting luminance/saturation
        const brand500 = base.darken(0.5).hex(); // Deep but vibrant
        const brand400 = base.darken(0.7).hex();
        const brand300 = base.darken(0.9).hex();
        const brand200 = base.darken(1.1).saturate(0.3).hex();
        const brand100 = base.darken(1.4).saturate(0.5).hex();
        const brand50 = base.darken(1.7).saturate(1).hex();

        const palette = {
          '--color-primary-brand': base.hex(),
          '--color-brand-500': brand500,
          '--color-brand-400': brand400,
          '--color-brand-300': brand300,
          '--color-brand-200': brand200,
          '--color-brand-100': brand100,
          '--color-brand-50': brand50,
          '--page-background': '#090909',
          '--container-background': '#111111',
          '--page-border': '#2A2A2A',
          '--card-color': '#1B1C1E',
          '--card-border': '#2A2A2A',
          '--filter-border': '#52525B',
          '--color-black': '#FFFFFF',
          '--color-white': '#000000',
          '--color-black-950': '#FDFDFD',
          '--color-black-900': '#EFEFEF',
          '--color-black-800': '#DCDCDC',
          '--color-black-700': '#BDBDBD',
          '--color-black-600': '#DEDEDE',
          '--color-black-500': '#7C7C7C',
          '--color-black-400': '#656565',
          '--color-black-300': '#525252',
          '--color-black-200': '#464646',
          '--color-black-150': '#3D3D3D',
          '--color-black-100': '#292929',
          '--color-black-50': '#292929',
          '--color-hyperlink': '#1460ED',
          '--color-active': '#27AE60',
          '--color-inactive': '#EB5757',
          '--color-assign': '#399761',
          '--color-note': '#FF881F',
          '--color-ai-gradient': '#1460ED',
        };
        return palette;
      },
      setSolidMode(baseColor) {
        // Solid: All brand colors are the same as the primary brand color
        const base = baseColor || '#2F80ED';
        const scale = chroma.scale([base, '#ffffff']).mode('lab').colors(6);
        const palette = {
          '--color-primary-brand': base,
          '--color-brand-500': scale[0],
          '--color-brand-400': scale[1],
          '--color-brand-300': scale[2],
          '--color-brand-200': scale[3],
          '--color-brand-100': scale[4],
          '--color-brand-50': scale[5],
          '--page-background': base,
          '--container-background': '#FFFFFF',
          '--page-border': '#E9EBEC',
          '--card-color': '#FFFFFF',
          '--card-border': '#E9EBEC',
          '--filter-border': '#CDCDCD',
          '--color-black': '#000000',
          '--color-white': '#FFFFFF',
          '--color-black-950': '#1F1F1F',
          '--color-black-900': '#EFEFEF',
          '--color-black-800': '#454545',
          '--color-black-700': '#4F4F4F',
          '--color-black-600': '#5D5D5D',
          '--color-black-500': '#6D6D6D',
          '--color-black-400': '#888888',
          '--color-black-300': '#B0B0B0',
          '--color-black-200': '#D1D1D1',
          '--color-black-150': '#EBEBEB',
          '--color-black-100': '#F1F1F1',
          '--color-black-50': '#F6F6F6',
          '--color-hyperlink': '#1460ED',
          '--color-active': '#27AE60',
          '--color-inactive': '#EB5757',
          '--color-assign': '#399761',
          '--color-note': '#FF881F',
          '--color-ai-gradient': '#1460ED',
        };
        return palette;
      },
      setSolidDarkMode(baseColor) {
        const base = chroma(baseColor || '#2F80ED');

        // Generate darker brand shades manually by adjusting luminance/saturation
        const brand500 = base.darken(0.5).hex(); // Deep but vibrant
        const brand400 = base.darken(0.7).hex();
        const brand300 = base.darken(0.9).hex();
        const brand200 = base.darken(1.1).saturate(0.3).hex();
        const brand100 = base.darken(1.4).saturate(0.5).hex();
        const brand50 = base.darken(1.7).saturate(1).hex();

        const palette = {
          '--color-primary-brand': base.hex(),
          '--color-brand-500': brand500,
          '--color-brand-400': brand400,
          '--color-brand-300': brand300,
          '--color-brand-200': brand200,
          '--color-brand-100': brand100,
          '--color-brand-50': brand50,
          '--page-background': '#090909',
          '--container-background': '#111111',
          '--page-border': '#2A2A2A',
          '--card-color': '#1B1C1E',
          '--card-border': '#2A2A2A',
          '--filter-border': '#52525B',
          '--color-black': '#FFFFFF',
          '--color-white': '#000000',
          '--color-black-950': '#FDFDFD',
          '--color-black-900': '#EFEFEF',
          '--color-black-800': '#DCDCDC',
          '--color-black-700': '#BDBDBD',
          '--color-black-600': '#DEDEDE',
          '--color-black-500': '#7C7C7C',
          '--color-black-400': '#656565',
          '--color-black-300': '#525252',
          '--color-black-200': '#464646',
          '--color-black-150': '#3D3D3D',
          '--color-black-100': '#292929',
          '--color-black-50': '#292929',
          '--color-hyperlink': '#1460ED',
          '--color-active': '#27AE60',
          '--color-inactive': '#EB5757',
          '--color-assign': '#399761',
          '--color-note': '#FF881F',
          '--color-ai-gradient': '#1460ED',
        };
        return palette;
      },
      setSolidGradientMode(baseColor) {
        // Solid Gradient: All brand colors are the same as the primary brand color, but page background is the brand color
        const base = baseColor || '#2F80ED';
        const scale = chroma.scale([base, '#ffffff']).mode('lab').colors(6);
        const palette = {
          '--color-primary-brand': base,
          '--color-brand-500': scale[0],
          '--color-brand-400': scale[1],
          '--color-brand-300': scale[2],
          '--color-brand-200': scale[3],
          '--color-brand-100': scale[4],
          '--color-brand-50': scale[5],
          '--page-background': base,
          '--container-background': '#FFFFFF',
          '--page-border': '#E9EBEC',
          '--card-color': '#FFFFFF',
          '--card-border': '#E9EBEC',
          '--filter-border': '#CDCDCD',
          '--color-black': '#000000',
          '--color-white': '#FFFFFF',
          '--color-black-950': '#1F1F1F',
          '--color-black-900': '#EFEFEF',
          '--color-black-800': '#454545',
          '--color-black-700': '#4F4F4F',
          '--color-black-600': '#5D5D5D',
          '--color-black-500': '#6D6D6D',
          '--color-black-400': '#888888',
          '--color-black-300': '#B0B0B0',
          '--color-black-200': '#D1D1D1',
          '--color-black-150': '#EBEBEB',
          '--color-black-100': '#F1F1F1',
          '--color-black-50': '#F6F6F6',
          '--color-hyperlink': '#1460ED',
          '--color-active': '#27AE60',
          '--color-inactive': '#EB5757',
          '--color-assign': '#399761',
          '--color-note': '#FF881F',
          '--color-ai-gradient': '#1460ED',
        };
        return palette;
      },
      setSolidGradientDarkMode(baseColor) {
        const base = chroma(baseColor || '#2F80ED');

        // Generate darker brand shades manually by adjusting luminance/saturation
        const brand500 = base.darken(0.5).hex(); // Deep but vibrant
        const brand400 = base.darken(0.7).hex();
        const brand300 = base.darken(0.9).hex();
        const brand200 = base.darken(1.1).saturate(0.3).hex();
        const brand100 = base.darken(1.4).saturate(0.5).hex();
        const brand50 = base.darken(1.7).saturate(1).hex();

        const palette = {
          '--color-primary-brand': base.hex(),
          '--color-brand-500': brand500,
          '--color-brand-400': brand400,
          '--color-brand-300': brand300,
          '--color-brand-200': brand200,
          '--color-brand-100': brand100,
          '--color-brand-50': brand50,
          '--page-background': '#090909',
          '--container-background': '#111111',
          '--page-border': '#2A2A2A',
          '--card-color': '#1B1C1E',
          '--card-border': '#2A2A2A',
          '--filter-border': '#52525B',
          '--color-black': '#FFFFFF',
          '--color-white': '#000000',
          '--color-black-950': '#FDFDFD',
          '--color-black-900': '#EFEFEF',
          '--color-black-800': '#DCDCDC',
          '--color-black-700': '#BDBDBD',
          '--color-black-600': '#DEDEDE',
          '--color-black-500': '#7C7C7C',
          '--color-black-400': '#656565',
          '--color-black-300': '#525252',
          '--color-black-200': '#464646',
          '--color-black-150': '#3D3D3D',
          '--color-black-100': '#292929',
          '--color-black-50': '#292929',
          '--color-hyperlink': '#1460ED',
          '--color-active': '#27AE60',
          '--color-inactive': '#EB5757',
          '--color-assign': '#399761',
          '--color-note': '#FF881F',
          '--color-ai-gradient': '#1460ED',
        };
        return palette;
      },
      setLightTheme() {
        this.themeMode = 'light';
        if (this.themeType == 'transparent') {
          this.palette = this.setLightMode(this.customColor);
        } else if (this.themeType == 'solid') {
          this.palette = this.setSolidMode(this.customColor);
        } else {
          this.palette = this.setSolidGradientMode(this.customColor);
        }
        this.applyColors(this.palette);
      },
      setDarkTheme() {
        this.themeMode = 'dark';
        if (this.themeType == 'transparent') {
          this.palette = this.setDarkMode(this.customColor);
        } else if (this.themeType == 'solid') {
          this.palette = this.setSolidDarkMode(this.customColor);
        } else {
          this.palette = this.setSolidGradientDarkMode(this.customColor);
        }
        this.palette = this.setDarkMode(this.customColor);
        this.applyColors(this.palette);
      },
      async initializeTheme() {
        try {
          await this.fetchThemes();
          this.currentTheme = this.getCurrentTheme || '';
          if (this.currentTheme) {
            this.applyTheme(this.currentTheme);
          }
        } catch (error) {
          console.error('Failed to initialize theme:', error);
        }
      },
      formatThemeName(name) {
        return name
          .replace(/([A-Z])/g, ' $1')
          .replace(/^./, (str) => str.toUpperCase())
          .trim();
      },
      applyColors(colors) {
        const colorMap = {
          '--color-primary-brand': '--primary-color-brand',
          '--color-brand-500': '--brand-500',
          '--color-brand-400': '--brand-400',
          '--color-brand-300': '--brand-300',
          '--color-brand-200': '--brand-200',
          '--color-brand-100': '--brand-100',
          '--color-brand-50': '--brand-50',
          '--color-black-950': '--black-950',
          '--color-black-900': '--black-900',
          '--color-black-800': '--black-800',
          '--color-black-700': '--black-700',
          '--color-black-600': '--black-600',
          '--color-black-500': '--black-500',
          '--color-black-400': '--black-400',
          '--color-black-300': '--black-300',
          '--color-black-200': '--black-200',
          '--color-black-150': '--black-150',
          '--color-black-100': '--black-100',
          '--color-black-50': '--black-50',
          '--page-background': '--page-background',
          '--container-background': '--container-background',
          '--page-border': '--page-border',
          '--card-color': '--card-color',
          '--card-border': '--card-border',
          '--filter-border': '--filter-border',
          '--color-black': '--color-black',
          '--color-white': '--color-white',
          '--color-hyperlink': '--hyper-links-blue',
          '--color-active': '--active-color',
          '--color-inactive': '--inactive-color',
          '--color-assign': '--green-assign-color',
          '--color-note': '--note-orange-color',
          '--color-ai-gradient': '--ai-button-gradient',
        };

        Object.entries(colors).forEach(([key, value]) => {
          const cssVar = colorMap[key] || key;
          document.documentElement.style.setProperty(cssVar, value);
        });
      },
      applyTextSettings() {
        // Apply text size and weight settings
        document.documentElement.style.setProperty('--text-LabelSize', this.textSettings.labelSize);
        document.documentElement.style.setProperty('--text-DataSize', this.textSettings.dataSize);
        document.documentElement.style.setProperty(
          '--font-weight-LabelWeight',
          this.textSettings.labelWeight
        );
        document.documentElement.style.setProperty(
          '--font-weight-DataWeight',
          this.textSettings.dataWeight
        );
        document.documentElement.style.setProperty(
          '--color-label-color',
          this.textSettings.labelColor
        );
        document.documentElement.style.setProperty(
          '--color-data-color',
          this.textSettings.dataColor
        );
      },
      applyTheme(themeName) {
        if (typeof themeName === 'string') {
          const theme = this.availableThemes.find((t) => t.name === themeName);
          if (theme && theme.colors) {
            this.applyColors(theme.colors);
          }
        } else if (typeof themeName === 'object' && themeName !== null) {
          this.applyColors(themeName);
        } else {
          const defaultColors = {
            '--color-primary-brand': '#178a7c',
            '--color-brand-500': '#21ab98',
            '--color-brand-400': '#3ac7b1',
            '--color-brand-300': '#69dfca',
            '--color-brand-200': '#a0efde',
            '--color-brand-100': '#d0f7ee',
            '--color-brand-50': '#f1fcf9',
            '--color-black-950': '#333333',
            '--color-black-900': '#2F80ED',
            '--color-black-800': '#2F80ED',
            '--color-black-700': '#2F80ED',
            '--color-black-600': '#2F80ED',
            '--color-black-500': '#2F80ED',
            '--color-black-400': '#2F80ED',
            '--color-black-300': '#2F80ED',
            '--color-black-200': '#2F80ED',
            '--color-black-150': '#2F80ED',
            '--color-black-100': '#2F80ED',
            '--color-black-50': '#2F80ED',
            '--color-hyperlink': '#2F80ED',
            '--color-active': '#2F80ED',
            '--color-inactive': '#2F80ED',
            '--color-assign': '#2F80ED',
            '--color-note': '#2F80ED',
          };
          this.applyColors(defaultColors);
        }
      },
      async saveChanges() {
        try {
          // Apply text settings before saving
          this.applyTextSettings();

          // Map solid_gradient to gradient for backend compatibility
          let backendThemeType = this.themeType || 'transparent';
          let paletteToSend = { ...this.palette };

          if (this.themeType === 'solid_gradient') {
            backendThemeType = 'gradient';
            // Add required gradient properties for backend validation
            paletteToSend = {
              ...this.palette,
              primary: this.palette['--color-primary-brand'] || this.customColor,
              secondary: this.palette['--color-brand-500'] || this.customColor,
              direction: 'linear',
            };
          }

          await this.updateTheme({
            theme: paletteToSend,
            themeType: backendThemeType,
            themeMode: this.themeMode || 'light',
            textSettings: this.textSettings,
          });
        } catch (error) {
          this.currentTheme = this.getCurrentTheme;
          this.applyTheme(this.getCurrentTheme);
        }
      },
      async selectTheme(name) {
        try {
          this.applyTheme(name);
          if (name) {
            const matchingTheme = this.availableThemes.find((theme) => theme.name === name);
            if (matchingTheme && matchingTheme.colors) {
              this.palette = matchingTheme.colors;
              this.customColor = this.palette['--color-primary-brand'];
              this.createPalatte();
            }
          }
          this.currentTheme = name;
        } catch (error) {
          this.currentTheme = this.getCurrentTheme;
          this.applyTheme(this.getCurrentTheme);
        }
      },
      async cancelSaved() {
        this.applyColors(this.getCurrentTheme);
        // Reset text settings to store values or default values
        if (this.getTextSettings) {
          this.textSettings = { ...this.getTextSettings };
        } else {
          this.textSettings = {
            labelSize: '14px',
            dataSize: '16px',
            labelWeight: '400',
            dataWeight: '400',
            labelColor: '#6D6D6D',
            dataColor: '#4F4F4F',
          };
        }
        this.applyTextSettings();
      },
    },
  };
</script>

<style scoped>
  .theme-picker {
    margin: 0.5em 0;
  }

  .theme-select {
    padding: 0.5em 1em;
    border-radius: 4px;
    border: 1px solid #ccc;
    font-size: 1em;
    cursor: pointer;
  }

  .theme-select:focus {
    outline: none;
    border-color: #666;
  }
</style>
